import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/store/AuthStore";
import { useApp } from "@/store/AppStore";
import ProfileContactModal from "@/components/layout/ProfileContactModal";
import ChangePasswordModal from "@/components/layout/ChangePasswordModal";
import { changeOwnPassword } from "@/store/api";

const UserMenu = () => {
  const { user, profile, signOut } = useAuth();
  const { updateProfileContact, pushNotification } = useApp();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const isAdmin = profile?.role === "admin";
  const fullName =
    profile && `${profile.names} ${profile.surnames}`.trim()
      ? `${profile.names} ${profile.surnames}`.trim()
      : user?.email ?? "Usuario";
  const initial = (profile?.names || user?.email || "U").charAt(0).toUpperCase();

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full border border-accent-500/20 bg-background-200/50 hover:border-accent-500/40 cursor-pointer"
        aria-label="Cuenta de usuario"
      >
        <span className="w-7 h-7 rounded-full bg-gradient-to-br from-accent-500 to-primary-600 text-background-50 flex items-center justify-center text-xs font-bold">
          {initial}
        </span>
        <span className="hidden md:block text-xs font-semibold text-foreground-800 max-w-[120px] truncate">
          {fullName}
        </span>
        <span className="hidden md:flex w-4 h-4 items-center justify-center text-foreground-500">
          <i className="ri-arrow-down-s-line text-sm" />
        </span>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[260px] glass-panel-strong rounded-lg overflow-hidden animate-slide-down z-50">
          <div className="px-4 py-3 border-b border-accent-500/15">
            <p className="text-sm font-semibold text-foreground-950 truncate">{fullName}</p>
            <p className="text-[11px] text-foreground-500 truncate">{user?.email}</p>
            <span
              className={`inline-flex items-center gap-1.5 mt-2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                isAdmin
                  ? "bg-accent-500/20 text-accent-200"
                  : "bg-secondary-500/25 text-secondary-200"
              }`}
            >
              <i className={isAdmin ? "ri-vip-crown-2-line" : "ri-user-star-line"} />
              {isAdmin ? "Administrador" : "Asesor / Distribuidor"}
            </span>
          </div>
          <button
            onClick={() => {
              setOpen(false);
              setProfileOpen(true);
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground-700 hover:bg-background-200/50 hover:text-foreground-950 cursor-pointer border-b border-background-300/20"
          >
            <span className="w-5 h-5 flex items-center justify-center text-accent-300">
              <i className="ri-user-settings-line" />
            </span>
            Mi perfil y contacto
          </button>
          <button
            onClick={() => {
              setOpen(false);
              setPwdOpen(true);
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground-700 hover:bg-background-200/50 hover:text-foreground-950 cursor-pointer border-b border-background-300/20"
          >
            <span className="w-5 h-5 flex items-center justify-center text-accent-300">
              <i className="ri-lock-password-line" />
            </span>
            Cambiar contraseña
          </button>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-foreground-700 hover:bg-background-200/50 hover:text-foreground-950 cursor-pointer"
          >
            <span className="w-5 h-5 flex items-center justify-center text-primary-200">
              <i className="ri-logout-box-r-line" />
            </span>
            Cerrar sesión
          </button>
        </div>
      )}

      <ProfileContactModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        title="Mi perfil y contacto"
        subtitle="Actualiza tu correo y tu teléfono."
        displayName={fullName}
        initial={{ email: profile?.email ?? "", phone: profile?.phone ?? "", address: "" }}
        showAddress={false}
        onSave={async (fields) => {
          const ok = await updateProfileContact(user?.id ?? "", {
            email: fields.email,
            phone: fields.phone,
          });
          // Aviso al administrador de que se actualizó el contacto.
          if (ok) {
            await pushNotification({
              audience: "admin",
              title: "Datos de contacto actualizados",
              message: `${fullName} actualizó su correo y/o teléfono.`,
              kind: "info",
              link: "/admin/asesores",
            });
          }
          return ok;
        }}
      />

      <ChangePasswordModal
        open={pwdOpen}
        onClose={() => setPwdOpen(false)}
        title="Cambiar mi contraseña"
        subtitle="Elige una contraseña nueva de al menos 6 caracteres."
        displayName={fullName}
        onSave={async (pwd) => {
          try {
            await changeOwnPassword(pwd);
            return true;
          } catch {
            return false;
          }
        }}
      />
    </div>
  );
};

export default UserMenu;